import { CloseButton, Flex, ModalBody, ModalFooter, Text, VStack } from '@chakra-ui/react'
import { RoundedButton } from '@components/Buttons'
import { DefaultModal } from '@components/Modals'
import { useProfile } from '@contexts/user/ProfileContext'
import { useCallback } from 'react'
import { useForm } from 'react-final-form'

interface TelehealthAcknowledgmentModalProps {
  isOpen: boolean
  onConfirm: () => void
  onDoNotBill: () => void
  onCancel: () => void
}

// §2.1: Acknowledgment modal shown when Routine Antepartum + Telehealth is selected.
// Three outcomes: confirm visit, flag as do-not-bill, or revert to in-person modality.
export const TelehealthAcknowledgmentModal = ({
  isOpen,
  onConfirm,
  onDoNotBill,
  onCancel,
}: TelehealthAcknowledgmentModalProps) => (
  <DefaultModal
    isOpen={isOpen}
    onClose={onCancel}
    noCloseButton
    size="2xl"
    header={
      <Flex align="center" w="100%" px={3}>
        <Text
          flex={1}
          minW={0}
          fontFamily="Hero-New"
          color="gray.700"
          fontWeight={500}
          fontSize="lg">
          Telehealth Visit — Confirmation Required
        </Text>
        <CloseButton onClick={onCancel} />
      </Flex>
    }
    render={() => (
      <>
        <ModalBody>
          <VStack align="flex-start" spacing={3}>
            <Text fontSize="md" color="gray.700" _dark={{ color: 'gray.300' }}>
              You have selected a telehealth modality for this routine antepartum visit. Please
              confirm how you would like to proceed.
            </Text>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Flex w="100%" gap={2} flexWrap="wrap" justify="flex-end">
            <RoundedButton size="sm" variant="outline" colorScheme="gray" onClick={onCancel}>
              Cancel — Change Visit Type
            </RoundedButton>
            <RoundedButton size="sm" variant="outline" colorScheme="orange" onClick={onDoNotBill}>
              Do Not Submit for Billing
            </RoundedButton>
            <RoundedButton size="sm" onClick={onConfirm}>
              Confirm Telehealth Visit
            </RoundedButton>
          </Flex>
        </ModalFooter>
      </>
    )}
  />
)

// Hook encapsulating the modal state and form interactions for the telehealth acknowledgment.
export const useTelehealthAcknowledgment = () => {
  const form = useForm()
  const { userId } = useProfile()

  const handleConfirm = useCallback(() => {
    form.change('data.visitLocation.telehealthAcknowledgedAt', new Date().toISOString())
    form.change('data.visitLocation.telehealthAcknowledgedBy', userId ?? '')
  }, [form, userId])

  const handleDoNotBill = useCallback(() => {
    form.change('data.doNotBill', true)
  }, [form])

  const handleCancel = useCallback(() => {
    form.change('data.visitLocation.modality', 'in-person')
  }, [form])

  return { handleConfirm, handleDoNotBill, handleCancel }
}
